import { Box, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useChannelStateContext } from "stream-chat-react";
import { useState } from "react";
import { ChatChannelActions } from "Components/Chat/ChatChannelActions";
import { ChatChannelAvatarWithName } from "Components/Chat/ChatChannelAvatarWithName";
import { ChatFindInConversationBar } from "Components/Chat/ChatFindInConversationBar";
import { getChatMemberProfileUrl } from "Components/Chat/chatConstants";
import { useChatChannelDisplayInfo } from "Hooks/Chat/useChatChannelDisplayInfo";
import { Resources, useResource } from "Translations/Resources";
import { ChannelEligibility } from "Types/Chat/channelEligibility";

type Props = { onBack?: () => void };

export const ChatChannelHeader = ({ onBack }: Props) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isFindOpen, setIsFindOpen] = useState(false);

  const { t } = useResource();
  const { channel } = useChannelStateContext();

  const currentUserId = channel.state.membership?.user?.id;
  const {
    displayName,
    avatarImage,
    isGroup,
    isOnline,
    accessRole,
    otherMembers,
    isAnonymized,
  } = useChatChannelDisplayInfo(channel, currentUserId);

  // Only a direct channel's title is a person. A group's is a channel name, and an anonymised
  // channel's counterpart must stay unidentifiable (ADR-0008) — no link, not even a dead one.
  const counterpartStreamId = otherMembers[0]?.user_id;
  const profileUrl =
    !isGroup && !isAnonymized && counterpartStreamId
      ? getChatMemberProfileUrl(counterpartStreamId)
      : undefined;

  // Direct channels get the menu too (starting a group from a 1:1); the only channel with no
  // actions at all is a direct one whose counterpart could not be resolved.
  const isActionsMenuVisible = isGroup || otherMembers.length > 0;
  const isFullMooneOnly =
    isGroup && channel.data?.eligibility === ChannelEligibility.FullMooneOnly;

  const handleMenuOpen = ({ currentTarget }: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleFindOpen = () => {
    setMenuAnchorEl(null);
    setIsFindOpen(true);
  };

  if (isFindOpen) {
    return <ChatFindInConversationBar onClose={() => setIsFindOpen(false)} />;
  }

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        bgcolor="primary.main"
        p={2.5}
        pl={onBack ? 0 : undefined}
        gap={1}
        flexShrink={0}
        minWidth={0}
        maxWidth="100vw"
        overflow="hidden"
      >
        {onBack && (
          <IconButton onClick={onBack} sx={{ color: "common.white" }}>
            <ChevronLeftIcon />
          </IconButton>
        )}
        <ChatChannelAvatarWithName
          isGroup={isGroup}
          isOnline={isOnline}
          accessRole={accessRole}
          avatarImage={avatarImage}
          displayName={displayName}
          otherMembers={otherMembers}
          profileUrl={profileUrl}
          isFullMooneOnly={isFullMooneOnly}
        />
        {isActionsMenuVisible && (
          <IconButton
            onClick={handleMenuOpen}
            sx={{ color: "common.white" }}
            aria-label={t(Resources.Common.MoreOptions)}
          >
            <MoreVertIcon />
          </IconButton>
        )}
      </Box>
      <ChatChannelActions
        anchorEl={menuAnchorEl}
        onClose={handleMenuClose}
        onFindInConversation={handleFindOpen}
        channelId={channel.id ?? ""}
      />
    </>
  );
};
